// Pure timeline operations for Studio mode.
// All functions are PURE: no side effects, no mutation of inputs.

import type { SceneClip, StudioTimeline, TransitionClip } from './types'
import type { Time } from './types'

/** End of a clip (exclusive). */
export function clipEnd(clip: { startTime: Time; duration: Time }): Time {
    return clip.startTime + clip.duration
}

/** Does `t` fall inside this clip's [startTime, startTime+duration)? */
export function clipContains(clip: { startTime: Time; duration: Time }, t: Time): boolean {
    return t >= clip.startTime && t < clip.startTime + clip.duration
}

/** Find the active scene clip at time `t`. Returns null if none. */
export function findActiveSceneAt(timeline: StudioTimeline, t: Time): SceneClip | null {
    for (const clip of timeline.sceneTrack.clips) {
        if (!clip.enabled) continue
        if (clipContains(clip, t)) return clip
    }
    return null
}

/** Find the active transition clip at time `t`. Returns null if none. */
export function findActiveTransitionAt(timeline: StudioTimeline, t: Time): TransitionClip | null {
    for (const clip of timeline.transitionTrack.clips) {
        if (!clip.enabled) continue
        if (clipContains(clip, t)) return clip
    }
    return null
}

/** Total duration of the timeline — rightmost clip edge across all tracks. */
export function timelineDuration(timeline: StudioTimeline): Time {
    let max = 0
    for (const clip of timeline.sceneTrack.clips) {
        const end = clipEnd(clip)
        if (end > max) max = end
    }
    for (const clip of timeline.transitionTrack.clips) {
        const end = clipEnd(clip)
        if (end > max) max = end
    }
    return max
}

/** Sort scene clips by startTime. Returns a new array. */
export function sortedSceneClips(timeline: StudioTimeline): SceneClip[] {
    return [...timeline.sceneTrack.clips].sort((a, b) => a.startTime - b.startTime)
}

/** Find the scene clip before the given one (by startTime). */
export function previousScene(timeline: StudioTimeline, clipId: string): SceneClip | null {
    const sorted = sortedSceneClips(timeline)
    const idx = sorted.findIndex((c) => c.id === clipId)
    return idx > 0 ? sorted[idx - 1] : null
}

/** Find the scene clip after the given one (by startTime). */
export function nextScene(timeline: StudioTimeline, clipId: string): SceneClip | null {
    const sorted = sortedSceneClips(timeline)
    const idx = sorted.findIndex((c) => c.id === clipId)
    return idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null
}

/** Find a transition that bridges two specific scene clips. */
export function findTransitionBetween(
    timeline: StudioTimeline,
    fromSceneId: string,
    toSceneId: string,
): TransitionClip | null {
    return (
        timeline.transitionTrack.clips.find(
            (t) => t.fromSceneId === fromSceneId && t.toSceneId === toSceneId,
        ) ?? null
    )
}

/** Get the transition progress (0..1) at time `t` for a transition clip. */
export function transitionProgress(clip: TransitionClip, t: Time): number {
    if (!clipContains(clip, t)) return 0
    return (t - clip.startTime) / clip.duration
}

/** Beat-grid cell size in seconds: one beat, grouped (×2, ×4, …) until the cell is
 *  wide enough to read/snap to at the given zoom (pixels-per-second). */
export function gridStepFor(bpm: number, pps: number): Time {
    const beatDur = 60 / Math.max(20, bpm)
    let beatsPerCell = 1
    while (beatDur * beatsPerCell * pps < 16) beatsPerCell *= 2
    return beatDur * beatsPerCell
}

/** Snap a time to the nearest grid cell line (given the cell size and beat-1 offset). */
export function snapTimeToGrid(t: Time, step: Time, offset: Time): Time {
    if (step <= 0) return Math.max(0, t)
    return Math.max(0, Math.round((t - offset) / step) * step + offset)
}